import { ConfigProvider, notification, type ThemeConfig } from 'antd';
import type { ArgsProps } from 'antd/es/notification';
import { useMemo, type ReactNode } from 'react';
import { getTheme } from '@/lib/antd/antd-theme';
import { useEffectiveThemeMode } from '@shared/store/theme.store';
import './notification.css';
import { appAlertIcons } from '../alert.utils';

// Static notification/message/modal render in their own root, outside PublicProviders' ConfigProvider.
// notification.css is tuned for antd's default tokens, so light mode keeps them; dark mode only swaps in the
// dark algorithm and surface/text colors. Subscribed to the store so a mode switch re-themes the root.
function StaticHolderTheme({ children }: { children: ReactNode }) {
  const mode = useEffectiveThemeMode();
  // Always an object: ConfigProvider only adds its token provider when `theme` is set, so toggling to undefined would
  // change the tree, remount antd's global holder and leave the static API pointing at the dead instance.
  const theme = useMemo<ThemeConfig>(() => {
    if (mode === 'light') return {};
    const { algorithm, token } = getTheme(mode);
    return { algorithm, token: { colorBgElevated: token?.colorBgElevated, colorText: token?.colorText } };
  }, [mode]);

  return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
}

ConfigProvider.config({ holderRender: (children) => <StaticHolderTheme>{children}</StaticHolderTheme> });

const METHODS = ['success', 'info', 'warning', 'error'] as const;

METHODS.forEach((method) => {
  const original = notification[method];

  notification[method] = ((args: ArgsProps) => {
    const enhancedIcon = appAlertIcons[method];

    return original({
      className: 'calilio-notification',
      icon: enhancedIcon,
      placement: 'top',
      duration: 5,
      ...args,
    });
  }) as (typeof notification)[typeof method];
});
